/** Small presentational pieces shared by the trend list rows and the trend song drawer. */
import { useId } from 'react';
import { ArrowDownRight, ArrowUpRight, Flame, Info, Minus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/components/ui/utils';
import type { TrendStatus } from '@/api/audioStudioApi';
import { ORIGINALS_NOTE, RIGHTS, type RightsKind } from './rights';
import { TREND_STATUS_META, formatRankChange, sparklinePoints } from './trendInsights';

/** Rank movement over the period: ▲ climbed, ▼ dropped, — flat, "New" when unknown; moves over 99 read "99+". */
export function RankChange({ v }: { v: number | null }) {
  const r = formatRankChange(v);
  if (r.move === 'new') return <span className="rounded-full bg-[#ecfdf3] px-1.5 py-0.5 text-[10px] text-[#027a48]" style={{ fontWeight: 600 }} aria-label={r.aria}>New</span>;
  if (r.move === 'flat') return <Minus className="h-3 w-3 text-[#98a2b3]" aria-label={r.aria} />;
  return r.move === 'up'
    ? <span className="flex items-center text-[11.5px] tabular-nums text-[#027a48]" style={{ fontWeight: 500 }} aria-label={r.aria}><ArrowUpRight className="h-3 w-3" aria-hidden />{r.label}</span>
    : <span className="flex items-center text-[11.5px] tabular-nums text-[#b42318]" style={{ fontWeight: 500 }} aria-label={r.aria}><ArrowDownRight className="h-3 w-3" aria-hidden />{r.label}</span>;
}

/** Trend status pill; "Rising fast" gets a flame so it stands out in a long list. Renders nothing when unknown. */
export function TrendStatusBadge({ status, className }: { status: TrendStatus | null | undefined; className?: string }) {
  const meta = status ? TREND_STATUS_META[status] : undefined;
  if (!meta) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10.5px]', meta.className, className)} style={{ fontWeight: 600 }}>
      {status === 'rising_fast' && <Flame className="h-3 w-3" aria-hidden />}
      {meta.label}
    </span>
  );
}

/** Card variant: plain text — "Rising fast" in colour with a flame, every other status neutral grey. */
export function TrendStatusText({ status }: { status: TrendStatus | null | undefined }) {
  const meta = status ? TREND_STATUS_META[status] : undefined;
  if (!meta) return null;
  const hot = status === 'rising_fast';
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-0.5 text-[11.5px]', hot ? 'text-[#c01048]' : 'text-[#667085]')} style={{ fontWeight: hot ? 600 : 500 }}>
      {hot && <Flame className="h-3 w-3" aria-hidden />}
      {meta.label}
    </span>
  );
}

export function GenreChips({ genres, max = 3, className }: { genres: string[]; max?: number; className?: string }) {
  if (!genres.length) return null;
  const shown = genres.slice(0, max);
  return (
    <span className={cn('flex min-w-0 flex-wrap gap-1', className)}>
      {shown.map((g) => (
        <span key={g} className="rounded-full bg-[#f2f4f7] px-1.5 py-0.5 text-[10.5px] text-[#475467]" style={{ fontWeight: 500 }}>{g}</span>
      ))}
      {genres.length > max && <span className="text-[10.5px] text-[#98a2b3]">+{genres.length - max}</span>}
    </span>
  );
}

/**
 * Tiny inline-SVG area sparkline of daily views (no chart library in the list rows). `fluid` stretches it to the
 * container's width (the geometry is drawn in a `width`×`height` box and scaled; strokes keep their thickness).
 */
export function TrendSparkline({ values, width = 84, height = 26, label, fluid = false }: { values: (number | null)[]; width?: number; height?: number; label?: string; fluid?: boolean }) {
  const gid = useId();
  const g = sparklinePoints(values, width, height);
  if (!g) return <span className="inline-block text-[11.5px] text-[#d0d5dd]" style={{ width: fluid ? '100%' : width, height }} aria-hidden />;
  return (
    <svg
      width={fluid ? '100%' : width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={fluid ? 'none' : undefined}
      role="img"
      aria-label={label || 'Daily views trend'}
      className={cn('shrink-0 overflow-visible', fluid && 'block w-full')}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#667085" stopOpacity={0.16} />
          <stop offset="100%" stopColor="#667085" stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={g.area} fill={`url(#${gid})`} />
      <path d={g.line} fill="none" stroke="#475467" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {!fluid && <circle cx={g.last[0]} cy={g.last[1]} r={2} fill="#475467" />}
    </svg>
  );
}

/**
 * Quiet grey usage-rights chip ("TikTok only" on the trend stage and drawer) with the explanation in a
 * tooltip (hover or keyboard focus). `onMedia` = sits on top of a cover (translucent white background).
 */
export function RightsChip({ kind, onMedia = false, className }: { kind: RightsKind; onMedia?: boolean; className?: string }) {
  const r = RIGHTS[kind];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          data-rights={kind}
          aria-label={`${r.label}: ${r.tooltip}`}
          className={cn(
            'inline-flex h-5 shrink-0 cursor-default items-center rounded-full px-1.5 text-[10.5px] leading-none text-[#475467] outline-none focus-visible:ring-2 focus-visible:ring-[#98a2b3]/40',
            onMedia ? 'bg-white/85 backdrop-blur-sm' : 'border border-[#eaecf0] bg-[#f9fafb]',
            className,
          )}
          style={{ fontWeight: 500 }}
          onClick={(e) => e.stopPropagation()}
        >
          {r.label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] bg-[#101828] text-[11.5px] leading-snug text-white">{r.tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** One quiet line above the team's sounds: they are all originals, with the rights explanation behind an ⓘ tooltip. */
export function OriginalsNote({ className }: { className?: string }) {
  return (
    <p data-testid="originals-note" className={cn('flex min-h-[18px] items-center gap-1 text-[12.5px] leading-[18px] text-[#667085]', className)}>
      <span className="min-w-0">{ORIGINALS_NOTE}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={`${RIGHTS.original.label}: ${RIGHTS.original.tooltip}`} className="shrink-0 rounded-full p-0.5 text-[#98a2b3] outline-none hover:text-[#475467] focus-visible:ring-2 focus-visible:ring-[#98a2b3]/40">
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] bg-[#101828] text-[11.5px] leading-snug text-white">{RIGHTS.original.tooltip}</TooltipContent>
      </Tooltip>
    </p>
  );
}
